import { IInvoicingItem } from '@/interfaces/IInvoicingItem';
import { IOrderItem } from '@/interfaces/IOrderItem';
import { IOrder } from '@/interfaces/IOrder';
import { IBranch } from '@/interfaces/IBranch';
import {
  buildArAfipInvoicePdf,
  buildInvoicePdfFilename,
  IArAfipInvoicePdfLine,
} from '@/lib/invoicing/ar-afip-invoice-pdf';
import { buildArAfipEmitter } from '@/lib/invoicing/ar-afip-emitter';
import { deliverInvoicePdf } from '@/lib/invoicing/deliver-pdf';

export interface IDownloadInvoicePdfInput {
  invoicingItem: IInvoicingItem;
  orderItems: IOrderItem[] | null;
  order: IOrder;
  branch: IBranch | null;
  currencySymbol: string;
  /**
   * Modal opening is injected: this module must work from both script-setup
   * and class components, and useModal() throws outside a setup context.
   */
  openPreview: (blob: Blob, filename: string) => void;
}

/**
 * Generates the ARCA/AFIP invoice PDF client-side and delivers it per
 * platform (native share sheet on Capacitor, preview modal when the browser
 * can render PDFs inline, direct download otherwise).
 * Plain module function (no composable state); callers handle toasts/loading.
 */
export async function downloadInvoicePdf(
  input: IDownloadInvoicePdfInput
): Promise<void> {
  const {
    invoicingItem,
    orderItems,
    order,
    branch,
    currencySymbol,
    openPreview,
  } = input;

  if (invoicingItem.invoicingData?.platform !== 'ar-afip') {
    throw new Error('Formato de factura no soportado');
  }

  const config: Record<string, unknown> =
    invoicingItem.invoicingData.data ?? {};
  const invoiceData: Record<string, unknown> = invoicingItem.data ?? {};

  const lines: IArAfipInvoicePdfLine[] = (orderItems ?? [])
    .filter((oi) => !oi.canceledAt)
    .map((oi) => ({
      description: oi.item?.name ?? '',
      amount: oi.amount,
      unitPrice: oi.price,
      total: oi.total,
    }));

  const docDefinition = buildArAfipInvoicePdf({
    invoice: { data: invoiceData, total: invoicingItem.total },
    emitter: buildArAfipEmitter(config, branch?.address ?? null),
    lines,
    adjustments: {
      delivery: order.deliveryCost ?? 0,
      discount: order.discountedAmount ?? 0,
      roundDifference: order.roundDifference ?? 0,
    },
    currencySymbol,
  });

  // Deliberate dynamic import: keeps pdfmake (~2 MB) out of the main bundle.
  const { generatePdfBlob } = await import('@/lib/invoicing/pdf-engine');
  const blob = await generatePdfBlob(docDefinition);
  const filename = buildInvoicePdfFilename(invoiceData);

  await deliverInvoicePdf({ blob, filename, openPreview });
}
